<template>
  <div ref="container" class="clinic-calendar-widget" />
</template>

<script setup lang="ts">
import {
  onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { mountCalendar } from '@/calendar/renderer';
import '@/calendar/calendar.css';

type CalendarContext = {
  id_staff?: number | null;
  id_branch?: number | null;
  id_patient?: number | null;
  date?: string;
};

type CalendarRequest = (
  event: 'snapshot' | 'command' | 'preview',
  payload: Record<string, unknown>,
  requestId: string,
) => Promise<unknown>;

type CalendarProps = {
  context: CalendarContext;
  request: CalendarRequest;
  refreshRevision?: number;
};

const props = defineProps<CalendarProps>();

type CalendarEvents = {
  (event: 'event', data: { event: string; payload: Record<string, unknown> }): void;
};

const emit = defineEmits<CalendarEvents>();

const container = ref<HTMLElement | null>(null);
let calendar: ReturnType<typeof mountCalendar> | null = null;

function refresh() {
  return calendar?.refresh() ?? Promise.resolve(false);
}

function setContext(context: CalendarContext) {
  return calendar?.setContext(context) ?? Promise.resolve(false);
}

onMounted(() => {
  if (!container.value) return;
  calendar = mountCalendar(container.value, {
    context: props.context,
    request: (
      event: Parameters<CalendarRequest>[0],
      payload: Record<string, unknown>,
      requestId: string,
    ) => props.request(event, payload, requestId),
    onEvent: (event: string, payload: Record<string, unknown>) => emit('event', { event, payload }),
  });
});

watch(() => props.context, setContext, { deep: true });
watch(() => props.refreshRevision, refresh);

onBeforeUnmount(() => {
  calendar?.destroy();
  calendar = null;
});

defineExpose({ refresh, setContext });
</script>
